import type { LocalizedString, PredefineMode, RuleWhenExecute } from '../schema';

/**
 * Output of one full rules evaluation pass. Every consumer (visibility,
 * disabled state, errors) reads its own slice. All collections are
 * frozen so callers can compare them by identity in React memos.
 */
export interface RuleResult {
    /** Characteristic IDs that should be hidden from the UI. */
    hiddenCharacteristics: ReadonlySet<number>;
    /** Value IDs that should be disabled (but still visible). */
    blacklistedValues: ReadonlySet<number>;
    /**
     * Predefine candidates per Characteristic ID — one slot each, last
     * matching rule wins. Candidates only: whether a candidate actually
     * becomes a write depends on its mode plus the current selection, which
     * is `resolvePredefineWrites` in `./predefine.ts`.
     */
    predefinedSelections: Readonly<Record<number, PredefinedSelection>>;
    /** Surfaced errors — currently shown in the BottomBar. */
    errors: ReadonlyArray<RuleError>;
    /** Warnings — soft notices, not blocking. */
    warnings: ReadonlyArray<RuleError>;
}

export interface PredefinedSelection {
    /**
     * The mode the admin picked for this Characteristic — `'1'` apply always,
     * `'2'` only on default value, `'3'` no selection or default value. Mode
     * `'0'` (Disabled) never reaches here; the evaluator drops it, matching
     * legacy's dependency filter (app/storage.js:884).
     */
    condition: Exclude<PredefineMode, '0'>;
    /**
     * Target Value ID, or `undefined` when the wire value isn't a Value ID —
     * which is the case for `dynamicValue` entries, kept so the resolver can
     * report them instead of writing something wrong.
     */
    valueId: number | undefined;
    /** Legacy dynamic target resolution — parsed, not implemented, skipped. */
    dynamicValue: boolean;
}

export interface RuleError {
    /** alt_title of the Characteristic the error refers to (when known). */
    characteristic: string | undefined;
    /** Display message — string for rulesgroups, LocalizedString for top-level rules. */
    message: string | LocalizedString;
    /** `'error'` blocks Add-to-Cart; `'warning'` is informational. */
    severity: 'error' | 'warning';
    /** Source rule id, useful for diagnostics + log correlation. */
    ruleId: number;
    /** `wizard` → the error holds the wizard on its step instead of only blocking the cart. */
    whenExecute?: RuleWhenExecute | undefined;
}

export const EMPTY_RULE_RESULT: RuleResult = Object.freeze({
    hiddenCharacteristics: new Set<number>(),
    blacklistedValues: new Set<number>(),
    predefinedSelections: {},
    errors: [],
    warnings: [],
});
